import { useMemo } from "react";
import {
  Bar,
  BarChart as RechartsBarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartEmptyState,
  ChartErrorState,
  ChartLoadingState,
} from "../ChartStates";
import { ChartBreakdownLegend, breakdownShares } from "../ChartBreakdownLegend";
import { cn } from "../../ui/utils";
import {
  AXIS_TICK_FONT_SIZE,
  AXIS_TICK_FONT_WEIGHT,
  BAR_FILL,
  CHART_PALETTE_LIGHT,
  MARK_CORNER_RADIUS,
  MARK_STROKE_WIDTH,
  formatCompact,
  formatNumber,
  markGlow,
} from "../chartUtils";

/**
 * Horizontal bar chart for a single measure ranked highest to lowest, where
 * each bar carries its own accent.
 *
 * The sibling {@link BarChart} colours by *series* — correct when bars are
 * grouped or stacked, since colour then means "which measure". Here there is
 * only one measure and colour means "which rank", so it has to vary per bar.
 * That is the whole reason this is a separate chart rather than a flag.
 */
/**
 * Space between a bar's cap and its count. Wide enough to clear the cap's
 * corner radius AND the glow bleeding past it, so the numeral sits on clean
 * background rather than in the halo.
 */
const LABEL_GAP = 10;

const CATEGORY_LINE_HEIGHT = 12;
const CATEGORY_GAP = 8;
/**
 * Rough advance width of a character as a share of the font size. Measuring
 * the real thing means a canvas or a DOM pass per tick per render; at these
 * sizes a ratio is within a character either way, and the cost of being wrong
 * is a label a touch short of the column rather than one over it.
 */
const CHAR_WIDTH_RATIO = 0.58;
const CATEGORY_MAX_LINES = 3;
/** Plot chrome the rows do NOT get: the two margins plus the value axis. */
const PLOT_CHROME_HEIGHT = 40;
/** Shortest a row can shrink to under `scrollable` before the chart scrolls instead of squeezing further. */
const MIN_SCROLLABLE_ROW_HEIGHT = 28;

/**
 * Wrap `raw` onto at most `maxLines` lines of about `maxChars`, marking the
 * last one with an ellipsis when anything had to be dropped.
 *
 * Breaks on whitespace, and cuts mid-word only for a single word too long for
 * the column — which is the one case where refusing to cut would overflow.
 */
const clampLabel = (raw: string, maxChars: number, maxLines: number): string[] => {
  const words = String(raw ?? "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];

  const lines: string[] = [];
  let line = "";
  let taken = 0;
  let cutMidWord = false;

  while (taken < words.length && lines.length < maxLines) {
    const word = words[taken];
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length <= maxChars) {
      line = candidate;
      taken += 1;
    } else if (line) {
      lines.push(line);
      line = "";
    } else {
      lines.push(word.slice(0, maxChars));
      taken += 1;
      cutMidWord = true;
    }
  }
  if (line && lines.length < maxLines) {
    lines.push(line);
    line = "";
  }

  // Anything left over — whole words, a half-written word, or a line that ran
  // out of rows — is marked, so a clipped name never reads as a complete one.
  if (taken < words.length || line || cutMidWord) {
    const last = lines[lines.length - 1] ?? "";
    lines[lines.length - 1] = `${last.slice(0, Math.max(1, maxChars - 3)).trimEnd()}...`;
  }
  return lines;
};

interface CategoryTickProps {
  /** Injected by recharts when it clones this element per tick. */
  x?: number;
  y?: number;
  payload?: { value?: unknown };
  chars: number;
  lines: number;
  className?: string;
}

/**
 * One clamped category label. Passed to `<YAxis tick>` as an element, which
 * recharts clones per tick with `x`, `y` and `payload` filled in — so the
 * sizing this needs travels as ordinary props.
 */
function CategoryTick({ x, y, payload, chars, lines, className }: Readonly<CategoryTickProps>) {
  const full = String(payload?.value ?? "");
  const rows = clampLabel(full, chars, lines);
  if (typeof x !== "number" || typeof y !== "number" || !rows.length) return <text />;
  return (
    <text
      x={x}
      y={y}
      textAnchor="end"
      fontSize={AXIS_TICK_FONT_SIZE}
      fontWeight={AXIS_TICK_FONT_WEIGHT}
      fill="var(--muted-foreground)"
      className={className}
    >
      {/* Native tooltip, so a clamped name is still readable in full. */}
      <title>{full}</title>
      {rows.map((row, i) => (
        <tspan
          key={`${row}-${i}`}
          x={x}
          // Centre the block on the tick: lift by half the stack, then step
          // down. The +4 drops the baseline to the middle of the line box.
          dy={i === 0 ? -((rows.length - 1) / 2) * CATEGORY_LINE_HEIGHT + 4 : CATEGORY_LINE_HEIGHT}
        >
          {row}
        </tspan>
      ))}
    </text>
  );
}

export interface RankedBarDatum {
  name: string;
  value: number;
  color?: string;
}

export interface RankedBarChartProps {
  data: RankedBarDatum[];
  height?: number;
  /** Horizontal (default) ranks bars top-to-bottom; vertical ranks columns left-to-right, for a card too narrow for row labels. */
  orientation?: "horizontal" | "vertical";
  palette?: readonly string[];
  loading?: boolean;
  error?: string | Error | null;
  onRetry?: () => void;
  emptyMessage?: string;
  categoryWidth?: number;
  barSize?: number;
  valueFormatter?: (value: number) => string;
  tooltipValueFormatter?: (value: number) => string;
  valueLabel?: string;
  /** When set, the tooltip shows the bar name plus this description instead of the count. */
  tooltipDescription?: (name: string) => string;
  /**
   * Show the colour-coded breakdown legend beside the plot: one row per bar
   * carrying its value and share of the total. Off by default, because the
   * bars already carry their category labels on the axis -- turn it on when
   * the shares are part of the reading, not just the ranking.
   */
  showBreakdownLegend?: boolean;
  /**
   * Denominator for each row's share. Omit to take shares over the shown bars.
   * Pass a grand total when these are a Top-N slice of something larger, so
   * the shares correctly do NOT sum to 100.
   */
  breakdownTotal?: number;
  onBarClick?: (datum: RankedBarDatum) => void;
  /** Grow to fill the parent flex column instead of a fixed `height` px. Requires a flex-column parent; `height` still sizes the loading/error/empty placeholders. */
  stretch?: boolean;
  /**
   * Horizontal orientation only: once there are enough bars that squeezing them into `height`
   * would shrink rows past a readable minimum, keep rows at that minimum and let the plot
   * scroll vertically within `height` instead of continuing to compress.
   */
  scrollable?: boolean;
  /**
   * Horizontal orientation only: minimum vertical gap in px between neighbouring bars. With
   * `scrollable`, rows hold at `barSize + rowGap` and the plot scrolls instead of compressing.
   */
  rowGap?: number;
  /** Class on the category (name) tick labels, e.g. an SVG `fill-*` utility to override the muted default. */
  categoryTickClassName?: string;
  className?: string;
  ariaLabel?: string;
  /** Title for the category axis (horizontal: the value axis at bottom; vertical: the category axis at top). */
  xAxisLabel?: string;
  /** Title for the value axis (horizontal: the category axis at left; vertical: the value axis at left). */
  yAxisLabel?: string;
}

export function RankedBarChart({
  data,
  height = 270,
  orientation = "horizontal",
  palette = CHART_PALETTE_LIGHT,
  loading,
  error,
  onRetry,
  emptyMessage = "No data available",
  categoryWidth = 110,
  barSize = 18,
  valueFormatter = formatCompact,
  tooltipValueFormatter = formatNumber,
  valueLabel = "Value",
  tooltipDescription,
  showBreakdownLegend = false,
  breakdownTotal,
  onBarClick,
  stretch = false,
  scrollable = false,
  rowGap,
  categoryTickClassName,
  className,
  ariaLabel,
  xAxisLabel,
  yAxisLabel,
}: RankedBarChartProps) {
  // Rank highest → lowest so the accent follows magnitude. Recharts draws the
  // first datum of a horizontal chart at the TOP, so no reversal is needed.
  const ranked = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    return [...rows]
      .sort((a, b) => b.value - a.value)
      .map((d, i) => ({
        name: d.name,
        value: d.value,
        color: d.color ?? palette[i % palette.length],
      }));
  }, [data, palette]);

  const errorMessage = error instanceof Error ? error.message : error;
  if (errorMessage) {
    return <ChartErrorState height={height} message={errorMessage} onRetry={onRetry} />;
  }
  if (loading) return <ChartLoadingState height={height} shape="cartesian" />;
  if (!ranked.length || ranked.every((d) => d.value <= 0)) {
    return <ChartEmptyState height={height} message={emptyMessage} shape="cartesian" />;
  }

  // How much label each row can hold: its own share of the plot decides the
  // line count, so the clamp tightens on a short chart or a long ranking
  // instead of letting neighbouring labels grow into each other.
  const naturalRowHeight = (height - PLOT_CHROME_HEIGHT) / Math.max(1, ranked.length);
  // Once rows would squeeze past a readable minimum, hold them there and let the plot grow
  // taller than `height` -- the wrapper below caps the visible area and scrolls the rest.
  const minScrollableRowHeight =
    rowGap === undefined ? MIN_SCROLLABLE_ROW_HEIGHT : Math.max(MIN_SCROLLABLE_ROW_HEIGHT, barSize + rowGap);
  const scrollActive =
    scrollable && orientation === "horizontal" && naturalRowHeight < minScrollableRowHeight;
  const rowHeight = Math.max(
    CATEGORY_LINE_HEIGHT,
    scrollActive ? minScrollableRowHeight : naturalRowHeight,
  );
  const scrollContentHeight = rowHeight * ranked.length + PLOT_CHROME_HEIGHT;
  const categoryLines = Math.max(
    1,
    Math.min(CATEGORY_MAX_LINES, Math.floor((rowHeight - 4) / CATEGORY_LINE_HEIGHT)),
  );
  const categoryChars = Math.max(
    6,
    Math.floor((categoryWidth - CATEGORY_GAP) / (AXIS_TICK_FONT_SIZE * CHAR_WIDTH_RATIO)),
  );

  // Shares over the grand total when these bars are a Top-N slice, otherwise
  // over the bars themselves (largest-remainder, so they sum to exactly 100).
  const sliceOfLargerSet = Boolean(breakdownTotal && breakdownTotal > 0);
  const shares = sliceOfLargerSet
    ? ranked.map((d) => Math.round((d.value / (breakdownTotal as number)) * 100))
    : breakdownShares(ranked.map((d) => d.value));

  // Shared between the plain horizontal chart and, under `scrollActive`, the scrolling half of
  // the split chart below -- the value axis is the only piece that differs between the two.
  const horizontalYAxis = (
    <YAxis
      type="category"
      dataKey="name"
      width={categoryWidth}
      tick={<CategoryTick chars={categoryChars} lines={categoryLines} className={categoryTickClassName} />}
      axisLine={false}
      tickLine={false}
      label={
        yAxisLabel
          ? {
              value: yAxisLabel,
              angle: -90,
              position: "left",
              offset: 16,
              style: { textAnchor: "middle" },
              fontSize: 11,
              fontWeight: 600,
              fill: "var(--muted-foreground)",
            }
          : undefined
      }
    />
  );
  const descriptionTooltip = ({ active, payload }: { active?: boolean; payload?: { payload?: { name?: string } }[] }) => {
    const name = payload?.[0]?.payload?.name;
    if (!active || !name || !tooltipDescription) return null;
    return (
      <div
        className="max-w-[260px] rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
      >
        <p className="font-semibold">{name}</p>
        <p className="mt-1 leading-snug">{tooltipDescription(name)}</p>
      </div>
    );
  };
  const horizontalTooltip = (
    <Tooltip
      cursor={{ fill: "var(--muted)", opacity: 0.4 }}
      formatter={(value: number) => [tooltipValueFormatter(value), valueLabel]}
      content={tooltipDescription ? descriptionTooltip : undefined}
      contentStyle={{
        background: "var(--popover)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        color: "var(--popover-foreground)",
        fontSize: 12,
      }}
      // Recharts inks tooltip text in the mark's fill; these bars are
      // outline-only, so both lines are pinned to the popover ink instead.
      labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
      itemStyle={{ color: "var(--popover-foreground)" }}
    />
  );
  const horizontalBarContent = (
    <>
      {/* Outline-only + glow, so even the shortest bar has a definite edge. */}
      {ranked.map((d) => (
        <Cell
          key={d.name}
          fill={BAR_FILL}
          stroke={d.color}
          strokeWidth={MARK_STROKE_WIDTH}
          style={{ filter: markGlow(d.color) }}
        />
      ))}

      {/* Drawn by hand: the Cell's SVG glow filter also applies to a
          recharts-rendered label, so owning the <text> lets it opt out
          with `filter: none`. */}
      <LabelList
        dataKey="value"
        content={(props: unknown) => {
          const { x, y, width, height, value } = (props ?? {}) as Record<
            string,
            number | undefined
          >;
          if (
            typeof x !== "number" ||
            typeof y !== "number" ||
            typeof width !== "number" ||
            typeof height !== "number" ||
            typeof value !== "number"
          ) {
            return null;
          }
          return (
            <text
              x={x + width + LABEL_GAP}
              y={y + height / 2}
              textAnchor="start"
              dominantBaseline="middle"
              fill="var(--foreground)"
              fontSize={11}
              fontWeight={700}
              style={{ filter: "none" }}
            >
              {valueFormatter(value)}
            </text>
          );
        }}
      />
    </>
  );

  return (
    <div
      className={cn("flex min-w-0 flex-col gap-2", stretch && "min-h-0 flex-1", className)}
      style={stretch ? undefined : { height }}
      aria-label={ariaLabel}
      role="img"
    >
      {showBreakdownLegend && (
        <ChartBreakdownLegend
          items={ranked.map((d, i) => ({
            name: d.name,
            color: d.color,
            value: d.value,
            share: shares[i] ?? 0,
          }))}
          valueFormatter={valueFormatter}
        />
      )}

      {scrollActive ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Scrolls on its own; the value axis below stays put instead of scrolling with it. */}
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto" style={{ scrollbarGutter: "stable" }}>
            <ResponsiveContainer width="100%" height={scrollContentHeight}>
              <RechartsBarChart
                data={ranked}
                layout="vertical"
                margin={{ top: 8, right: 36, bottom: 0, left: yAxisLabel ? 27 : 8 }}
              >
                <XAxis type="number" hide allowDecimals={false} />
                {horizontalYAxis}
                {horizontalTooltip}
                <Bar
                  dataKey="value"
                  barSize={barSize}
                  radius={[0, MARK_CORNER_RADIUS, MARK_CORNER_RADIUS, 0]}
                  isAnimationActive={false}
                  onClick={onBarClick ? (d: (typeof ranked)[number]) => onBarClick(d) : undefined}
                  className={onBarClick ? "cursor-pointer" : undefined}
                >
                  {horizontalBarContent}
                </Bar>
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
          {/* Fixed footer: same domain as the scrolling plot above (same `ranked` data and
              margins), so its ticks line up with the bars even though it never scrolls. */}
          <div className="shrink-0">
            <ResponsiveContainer width="100%" height={xAxisLabel ? 54 : 30}>
              <RechartsBarChart
                data={ranked}
                layout="vertical"
                margin={{ top: 0, right: 36, bottom: xAxisLabel ? 30 : 8, left: yAxisLabel ? 27 : 8 }}
              >
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{
                    fontSize: AXIS_TICK_FONT_SIZE,
                    fontWeight: AXIS_TICK_FONT_WEIGHT,
                    fill: "var(--muted-foreground)",
                  }}
                  tickFormatter={(value: number) => valueFormatter(value)}
                  axisLine={false}
                  tickLine={false}
                  label={
                    xAxisLabel
                      ? {
                          value: xAxisLabel,
                          position: "bottom",
                          offset: 10,
                          fontSize: 11,
                          fontWeight: 600,
                          fill: "var(--muted-foreground)",
                        }
                      : undefined
                  }
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={categoryWidth}
                  tick={false}
                  axisLine={false}
                  tickLine={false}
                />
                <Bar dataKey="value" barSize={0} fill="transparent" isAnimationActive={false} />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
      <div className="min-h-0 min-w-0 flex-1">
      <ResponsiveContainer width="100%" height="100%">
        {orientation === "vertical" ? (
          <RechartsBarChart
            data={ranked}
            layout="horizontal"
            margin={{ top: 24, right: 8, bottom: xAxisLabel ? 32 : 8, left: yAxisLabel ? 12 : 8 }}
          >
            <XAxis
              type="category"
              dataKey="name"
              interval={0}
              tick={{
                fontSize: AXIS_TICK_FONT_SIZE,
                fontWeight: AXIS_TICK_FONT_WEIGHT,
                fill: "var(--muted-foreground)",
              }}
              axisLine={false}
              tickLine={false}
              label={
                xAxisLabel
                  ? {
                      value: xAxisLabel,
                      position: "bottom",
                      offset: 10,
                      fontSize: 11,
                      fontWeight: 600,
                      fill: "var(--muted-foreground)",
                    }
                  : undefined
              }
            />
            <YAxis
              type="number"
              allowDecimals={false}
              tickFormatter={(value: number) => valueFormatter(value)}
              tick={{
                fontSize: AXIS_TICK_FONT_SIZE,
                fontWeight: AXIS_TICK_FONT_WEIGHT,
                fill: "var(--muted-foreground)",
              }}
              axisLine={false}
              tickLine={false}
              width={36}
              label={
                yAxisLabel
                  ? {
                      value: yAxisLabel,
                      angle: -90,
                      position: "insideLeft",
                      style: { textAnchor: "middle" },
                      fontSize: 11,
                      fontWeight: 600,
                      fill: "var(--muted-foreground)",
                    }
                  : undefined
              }
            />
            <Tooltip
              cursor={{ fill: "var(--muted)", opacity: 0.4 }}
              formatter={(value: number) => [tooltipValueFormatter(value), valueLabel]}
      content={tooltipDescription ? descriptionTooltip : undefined}
              contentStyle={{
                background: "var(--popover)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                color: "var(--popover-foreground)",
                fontSize: 12,
              }}
              labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
              itemStyle={{ color: "var(--popover-foreground)" }}
            />
            <Bar
              dataKey="value"
              barSize={barSize}
              // Rounded at the top (data end), square at the baseline.
              radius={[MARK_CORNER_RADIUS, MARK_CORNER_RADIUS, 0, 0]}
              isAnimationActive={false}
              onClick={onBarClick ? (d: (typeof ranked)[number]) => onBarClick(d) : undefined}
              className={onBarClick ? "cursor-pointer" : undefined}
            >
              {ranked.map((d) => (
                <Cell
                  key={d.name}
                  fill={BAR_FILL}
                  stroke={d.color}
                  strokeWidth={MARK_STROKE_WIDTH}
                  style={{ filter: markGlow(d.color) }}
                />
              ))}

              <LabelList
                dataKey="value"
                content={(props: unknown) => {
                  const { x, y, width, value } = (props ?? {}) as Record<
                    string,
                    number | undefined
                  >;
                  if (
                    typeof x !== "number" ||
                    typeof y !== "number" ||
                    typeof width !== "number" ||
                    typeof value !== "number"
                  ) {
                    return null;
                  }
                  return (
                    <text
                      x={x + width / 2}
                      y={y - LABEL_GAP}
                      textAnchor="middle"
                      fill="var(--foreground)"
                      fontSize={11}
                      fontWeight={700}
                      style={{ filter: "none" }}
                    >
                      {valueFormatter(value)}
                    </text>
                  );
                }}
              />
            </Bar>
          </RechartsBarChart>
        ) : (
          <RechartsBarChart
            data={ranked}
            layout="vertical"
            margin={{ top: 8, right: 36, bottom: xAxisLabel ? 30 : 8, left: yAxisLabel ? 27 : 8 }}
          >
            <XAxis
              type="number"
              allowDecimals={false}
              tick={{
                fontSize: AXIS_TICK_FONT_SIZE,
                fontWeight: AXIS_TICK_FONT_WEIGHT,
                fill: "var(--muted-foreground)",
              }}
              // Wrapped: recharts calls tickFormatter(value, index), and passing
              // formatCompact by reference used index as a unit suffix.
              tickFormatter={(value: number) => valueFormatter(value)}
              axisLine={false}
              tickLine={false}
              label={
                xAxisLabel
                  ? {
                      value: xAxisLabel,
                      position: "bottom",
                      offset: 10,
                      fontSize: 11,
                      fontWeight: 600,
                      fill: "var(--muted-foreground)",
                    }
                  : undefined
              }
            />
            {horizontalYAxis}
            {horizontalTooltip}
            <Bar
              dataKey="value"
              barSize={barSize}
              // Rounded at the data end, square at the axis it grows from.
              radius={[0, MARK_CORNER_RADIUS, MARK_CORNER_RADIUS, 0]}
              isAnimationActive={false}
              onClick={onBarClick ? (d: (typeof ranked)[number]) => onBarClick(d) : undefined}
              className={onBarClick ? "cursor-pointer" : undefined}
            >
              {horizontalBarContent}
            </Bar>
          </RechartsBarChart>
        )}
      </ResponsiveContainer>
      </div>
      )}
    </div>
  );
}
